//src/pages/inventory/Pharmacy/IssueMedicineModal.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Grid,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { deductMedicineInventory, getExistingBatchNumbers, getMedicineInventoryList } from "../../../services/medicineInventoryService";
import { createInvoice } from "../../../services/invoiceService";
import { getCostPerUnit, getCostPerUnitById } from "../../../services/medicineNewService"; // Import the new service
import { deductMedicineInventoryWithHistory } from "../../../services/medicineTransactionService";
import { getPatientById } from "../../../services/patientService"; // Import patient service to fetch patient name
import {
  resolveInvoiceDepartmentId,
  resolveInvoicePrescriptionId,
} from "../../../utils/invoiceRelationUtils";
import { useAuth } from "../../../context/AuthContext";
import { Alert, AlertTitle } from '@mui/material';
import { getOrganizationName, fetchAndStoreOrganizationName, getOrganizationId } from "../../../utils/organizationUtils";
import { getHospitalOrganizationById } from "../../../services/hospitalOrganizationService";
import { getOrganizationById } from "../../../services/organizationService";
import { usePharmacyExpiryThreshold } from "../../../hooks/usePharmacyExpiryThreshold";
import { triggerNearExpiryLowStockAlert } from "../../../services/pharmacySettingsService";
import { formatExpiryThresholdLabel } from "../../../utils/medicineExpiryThreshold";
import {
  evaluateMedicineIssueStock,
  hasBlockingStockIssues,
  isUsableInventoryBatch,
  resolveMedicineInventoryMatch,
  stockStatusLabel,
} from "../../../utils/medicineIssueStockCheck";
import {
  pickInventorySupplierFields,
  resolveSupplierForIssueInvoice,
} from "../../../utils/medicineIssueSupplierUtils";
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
  DENSE_FIELD_PROPS,
} from "../../../utils/formModalStyles";
import {
  buildOpdMedicineInvoiceDocumentHtml,
  printPatientSummaryDocument,
} from "../../../utils/patientSummaryDocumentUtils";
import {
  InvoiceAttachmentControl,
  InvoiceSummarySection,
  calcInvoiceGstBreakdown,
  formatInvoiceInr,
  resolveAttachmentUrl,
} from "../../../components/InvoiceFormShared";
import { useOrganizationGstPercentage } from "../../../hooks/useOrganizationGstPercentage";
import { uploadAttachment } from "../../../services/uploadService";
import dayjs from "dayjs";

const calculateTotalDosage = (dosage) => {
  if (!dosage) return 0;
  return dosage.split("-").reduce((sum, num) => sum + parseInt(num.trim() || 0, 10), 0);
};

const IssueMedicineModal = ({
  open,
  onClose,
  medicines,
  onSubmit,
  patientId,
  doctorName,
  doctorId = null,
  appointmentId = null,
  departmentId = null,
  prescriptionId = null,
}) => {
  const { user } = useAuth(); // Get current user for transaction tracking
  const { isNearExpiry, months: expiryThresholdMonths } = usePharmacyExpiryThreshold();
  const gstPercentage = useOrganizationGstPercentage(user, { enabled: open });
  const [durations, setDurations] = useState({});
  const [durationErrors, setDurationErrors] = useState({});
  const [totalAmount, setTotalAmount] = useState(0); // State to hold total amount
  const [sgst, setSgst] = useState(0); // State to hold SGST
  const [cgst, setCgst] = useState(0); // State to hold CGST
  const [invoiceDetails, setInvoiceDetails] = useState([]); // State to hold invoice details
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false); // State for review dialog
  const [invoiceCreated, setInvoiceCreated] = useState(false); // Print only after Confirm Invoice succeeds
  const [alertOpen, setAlertOpen] = useState(false); // State for alert popup
  const [alertMessage, setAlertMessage] = useState(""); // State for alert message
  const [alertType, setAlertType] = useState("success"); // State for alert type
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(0); // State for discount
  const [paymentMode, setPaymentMode] = useState({ mode: "cash" });
  const [particulars, setParticulars] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentName, setAttachmentName] = useState("");
  const [patientName, setPatientName] = useState(""); // State to hold patient name
  const [patientDetails, setPatientDetails] = useState(null);
  const [organizationName, setOrganizationName] = useState("ARYAN HOSPITAL"); // State to hold organization name, default to "ARYAN HOSPITAL"
  const [organizationAddress, setOrganizationAddress] = useState("123 Medical Street, Health City"); // State to hold organization address
  const [organizationPhone, setOrganizationPhone] = useState("+91 123-456-7890"); // State to hold organization phone
  const [organizationEmail, setOrganizationEmail] = useState("info@aryanhospital.com"); // State to hold organization email
  /** Medicines still selected for issue (pharmacy admin can remove unwanted items). */
  const [medicinesToIssue, setMedicinesToIssue] = useState([]);
  /** Cached inventory for pre-issue stock validation (UI only). */
  const [inventoryList, setInventoryList] = useState([]);
  const [stockChecking, setStockChecking] = useState(false);
  const [stockChecks, setStockChecks] = useState([]);
  const [stockLoadError, setStockLoadError] = useState("");

  // Reset form when the modal opens. Do not depend on `medicines` identity —
  // parent refresh after a successful issue would otherwise clear Print Invoice.
  useEffect(() => {
    if (open) {
      setMedicinesToIssue(Array.isArray(medicines) ? [...medicines] : []);
      setReviewDialogOpen(false);
      setInvoiceCreated(false);
      setInvoiceDetails([]);
      setTotalAmount(0);
      setDiscount(0);
      setPaymentMode({ mode: "cash" });
      setParticulars("");
      setAttachmentFile(null);
      setAttachmentName("");
      setStockChecks([]);
      setStockLoadError("");
    }
  }, [open]);

  // Load medicine inventory when modal opens (for stock attention / Confirm Issue gating)
  useEffect(() => {
    let cancelled = false;
    const loadInventory = async () => {
      if (!open) return;
      setStockChecking(true);
      setStockLoadError("");
      try {
        const list = await getMedicineInventoryList();
        if (!cancelled) {
          setInventoryList(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error("[IssueMedicineModal] Failed to load inventory for stock check:", err);
        if (!cancelled) {
          setInventoryList([]);
          setStockLoadError(
            "Unable to verify medicine inventory stock. Confirm Issue is disabled until stock can be checked."
          );
        }
      } finally {
        if (!cancelled) setStockChecking(false);
      }
    };
    loadInventory();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Re-evaluate stock whenever issue list or durations change
  useEffect(() => {
    if (!open) {
      setStockChecks([]);
      return;
    }
    if (stockLoadError) {
      setStockChecks([]);
      return;
    }

    const checks = medicinesToIssue.map((medicine) => {
      const requiredQty =
        calculateTotalDosage(medicine.dosage) *
        Math.max(0, Number(durations[medicine.name] || 0));
      return evaluateMedicineIssueStock({
        medicineId: medicine.medicineId,
        medicineName: medicine.name,
        requiredQty,
        inventoryList,
        allowMissingExpiry: true,
      });
    });
    setStockChecks(checks);
  }, [open, medicinesToIssue, durations, inventoryList, stockLoadError]);

  // Fetch organization details when modal opens
  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      if (open) {
        try {
          // First try to get organization name from user object or localStorage
          let orgName = getOrganizationName(user);
          const organizationId = getOrganizationId(user);
          
          // If we have organizationId, fetch full organization details
          if (organizationId && organizationId > 0) {
            try {
              // Try hospital organizations first
              try {
                const hospitalOrg = await getHospitalOrganizationById(organizationId);
                if (hospitalOrg) {
                  // Get organization name if not already set
                  if (!orgName) {
                    orgName = hospitalOrg.name || null;
                  }
                  
                  // Extract address
                  let address = null;
                  if (hospitalOrg.address) {
                    address = hospitalOrg.address;
                  } else if (hospitalOrg.location) {
                    address = hospitalOrg.location;
                  } else if (hospitalOrg.fullAddress) {
                    address = hospitalOrg.fullAddress;
                  } else {
                    // Construct address from separate fields
                    const addressParts = [];
                    if (hospitalOrg.streetAddress) addressParts.push(hospitalOrg.streetAddress);
                    if (hospitalOrg.city) addressParts.push(hospitalOrg.city);
                    if (hospitalOrg.state) addressParts.push(hospitalOrg.state);
                    if (hospitalOrg.pincode) addressParts.push(hospitalOrg.pincode);
                    if (hospitalOrg.zipCode) addressParts.push(hospitalOrg.zipCode);
                    
                    if (addressParts.length > 0) {
                      address = addressParts.join(", ");
                    }
                  }
                  
                  // Extract phone
                  const phone = hospitalOrg.phone || hospitalOrg.phoneNumber || hospitalOrg.mobile || hospitalOrg.contactNumber || null;
                  
                  // Extract email
                  const email = hospitalOrg.email || hospitalOrg.emailAddress || null;
                  
                  // Set all organization details
                  setOrganizationName(orgName || "ARYAN HOSPITAL");
                  setOrganizationAddress(address || "123 Medical Street, Health City");
                  setOrganizationPhone(phone || "+91 123-456-7890");
                  setOrganizationEmail(email || "info@aryanhospital.com");
                  
                  return; // Successfully fetched, exit
                }
              } catch (hospitalError) {
                // If hospital organization not found, try regular organizations
                console.log(`[IssueMedicineModal] Hospital organization not found, trying regular organizations...`);
              }
              
              // Try regular organizations
              const org = await getOrganizationById(organizationId);
              if (org) {
                // Get organization name if not already set
                if (!orgName) {
                  orgName = org.name || null;
                }
                
                // Extract address
                let address = null;
                if (org.address) {
                  address = org.address;
                } else if (org.location) {
                  address = org.location;
                } else if (org.fullAddress) {
                  address = org.fullAddress;
                } else {
                  // Construct address from separate fields
                  const addressParts = [];
                  if (org.streetAddress) addressParts.push(org.streetAddress);
                  if (org.city) addressParts.push(org.city);
                  if (org.state) addressParts.push(org.state);
                  if (org.pincode) addressParts.push(org.pincode);
                  if (org.zipCode) addressParts.push(org.zipCode);
                  
                  if (addressParts.length > 0) {
                    address = addressParts.join(", ");
                  }
                }
                
                // Extract phone
                const phone = org.phone || org.phoneNumber || org.mobile || org.contactNumber || null;
                
                // Extract email
                const email = org.email || org.emailAddress || null;
                
                // Set all organization details
                setOrganizationName(orgName || "ARYAN HOSPITAL");
                setOrganizationAddress(address || "123 Medical Street, Health City");
                setOrganizationPhone(phone || "+91 123-456-7890");
                setOrganizationEmail(email || "info@aryanhospital.com");
                
                return; // Successfully fetched, exit
              }
            } catch (orgError) {
              console.warn(`[IssueMedicineModal] Organization ${organizationId} not found in either service:`, orgError);
            }
            
            // If name was fetched but full details weren't, try to fetch name separately
            if (!orgName) {
              orgName = await fetchAndStoreOrganizationName(organizationId);
            }
            
            // If we have orgName but didn't fetch full details, set name and keep defaults for other fields
            if (orgName) {
              setOrganizationName(orgName);
              // Keep default values for address, phone, email if not already set
              setOrganizationAddress("123 Medical Street, Health City");
              setOrganizationPhone("+91 123-456-7890");
              setOrganizationEmail("info@aryanhospital.com");
            }
          } else {
            // No organizationId - basic user, keep all defaults
            setOrganizationName(orgName || "ARYAN HOSPITAL");
            setOrganizationAddress("123 Medical Street, Health City");
            setOrganizationPhone("+91 123-456-7890");
            setOrganizationEmail("info@aryanhospital.com");
          }
        } catch (error) {
          console.error(`[IssueMedicineModal] Error fetching organization details:`, error);
          // Keep default values on error
          setOrganizationName("ARYAN HOSPITAL");
          setOrganizationAddress("123 Medical Street, Health City");
          setOrganizationPhone("+91 123-456-7890");
          setOrganizationEmail("info@aryanhospital.com");
        }
      } else if (!open) {
        // Reset organization details when modal closes
        setOrganizationName("ARYAN HOSPITAL");
        setOrganizationAddress("123 Medical Street, Health City");
        setOrganizationPhone("+91 123-456-7890");
        setOrganizationEmail("info@aryanhospital.com");
      }
    };

    fetchOrganizationDetails();
  }, [open, user]);

  // Fetch patient name when modal opens or patientId changes
  useEffect(() => {
    const fetchPatientName = async () => {
      if (patientId && open) {
        try {
          const patient = await getPatientById(patientId);
          if (patient) {
            setPatientDetails(patient);
            // Extract patient name from patient object
            const name = patient.name || 
                       (patient.firstName && patient.lastName ? `${patient.firstName} ${patient.lastName}`.trim() : 
                       patient.firstName || patient.lastName || null);
            if (name) {
              setPatientName(name);
            } else {
              console.warn(`[IssueMedicineModal] Patient name not found for patientId: ${patientId}`);
              setPatientName(null);
            }
          }
        } catch (error) {
          console.error(`[IssueMedicineModal] Error fetching patient name for patientId ${patientId}:`, error);
          setPatientName(null);
          setPatientDetails(null);
        }
      } else if (!open) {
        // Reset patient name when modal closes
        setPatientName("");
        setPatientDetails(null);
      }
    };

    fetchPatientName();
  }, [patientId, open]);

  useEffect(() => {
    const initialDurations = medicinesToIssue.reduce((acc, medicine) => {
      acc[medicine.name] = medicine.duration || durations[medicine.name] || "";
      return acc;
    }, {});
    setDurations(initialDurations);
  }, [medicinesToIssue]);

  const handleRemoveMedicine = (indexToRemove) => {
    setMedicinesToIssue((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, index) => index !== indexToRemove);
    });
  };

  const showAlert = (message, type = "success") => {
    setAlertMessage(message);
    setAlertType(type);
    setAlertOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
    setAlertMessage("");
  };

  const handlePrint = () => {
    const discountedAmount = totalAmount - (discount || 0);
    const { gstAmount, totalAmount: finalAmount } =
      calcInvoiceGstBreakdown(discountedAmount, gstPercentage);
    const visitDate = dayjs().format("DD MMM YYYY");
    const patientFromDetails = patientDetails || {};
    const resolvedPatientName =
      patientName ||
      patientFromDetails.name ||
      [patientFromDetails.firstName, patientFromDetails.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      "Patient";

    const bodyHtml = buildOpdMedicineInvoiceDocumentHtml({
      hospitalName: organizationName,
      hospitalAddress: organizationAddress,
      hospitalPhone: organizationPhone,
      visitDate,
      doctorName: doctorName || "Doctor",
      patient: {
        id: patientId || patientFromDetails.id || "N/A",
        name: resolvedPatientName,
        gender: patientFromDetails.gender || "",
        age: patientFromDetails.age || "",
        dob: patientFromDetails.dob || "",
        mobile:
          patientFromDetails.mobile ||
          patientFromDetails.phone ||
          "",
        address: patientFromDetails.address || "",
      },
      medicines: (invoiceDetails || []).map((medicine) => ({
        name: medicine.name,
        quantity: medicine.totalQuantity,
        totalCost: medicine.totalCost,
        amountLabel: formatInvoiceInr(medicine.totalCost),
      })),
      summaryLines: [
        { label: "Subtotal (₹)", value: formatInvoiceInr(discountedAmount) },
        ...(discount > 0
          ? [
              {
                label: "Total Discount (₹)",
                value: formatInvoiceInr(discount),
              },
            ]
          : []),
        {
          label: `GST @ ${gstPercentage}% (₹)`,
          value: formatInvoiceInr(gstAmount),
        },
        {
          label: "Total Payable Amount (₹)",
          value: formatInvoiceInr(finalAmount),
        },
      ],
      notes:
        "This is a computer-generated invoice and does not require a signature.",
      prescriptionId: resolveInvoicePrescriptionId(prescriptionId) ?? "",
      appointmentId:
        appointmentId != null && appointmentId !== "" ? Number(appointmentId) : "",
    });

    printPatientSummaryDocument({
      title: "OPD Summary",
      bodyHtml,
    });
  };

  const handleSave = async () => {
  try {
    setLoading(true);
    
    if (medicinesToIssue.length === 0) {
      showAlert("Please keep at least one medicine to issue.", "error");
      setLoading(false);
      return;
    }

    // Validate durations: must be >= 1
    const invalid = medicinesToIssue.find(
      (m) => !durations[m.name] || Number(durations[m.name]) < 1
    );
    if (invalid) {
      showAlert("Duration must be at least 1 day for all medicines to issue.", "error");
      setLoading(false);
      return;
    }

    if (stockLoadError || stockChecking) {
      showAlert(
        stockLoadError || "Still verifying inventory stock. Please wait.",
        "error"
      );
      setLoading(false);
      return;
    }

    if (hasBlockingStockIssues(stockChecks)) {
      const notes = stockChecks
        .filter((c) => c.blocking)
        .map((c) => c.message)
        .join(" ");
      showAlert(
        `Cannot confirm issue until stock issues are resolved. ${notes}`,
        "error"
      );
      setLoading(false);
      return;
    }

    const medicineData = await Promise.all(medicinesToIssue.map(async (medicine, medIndex) => {
        // Log the medicine object to check if id is set
      // console.log("Selected Medicine:", medicine);
      // Prefer inventory-resolved ID (name fallback when prescription medicineId mismatches catalog)
      const stockResolvedId = stockChecks[medIndex]?.resolvedMedicineId;
      let resolvedMedicineId =
        stockResolvedId ?? medicine.medicineId ?? medicine.medicine_id ?? null;

      // Fetch batch numbers and expiry dates from full inventory list
      let batchNumber = medicine.batchNumber || "N/A";
      let expiryDate = medicine.expiryDate || null;
      let inventoryId = null; // Add inventory ID tracking
      let supplierFields = {
        supplierId: null,
        supplierName: null,
        stockInvoiceId: null,
      };
      
      try {
        // Fetch full medicine inventory list to get complete records with expiry dates
        const listForBatch = inventoryList?.length
          ? inventoryList
          : await getMedicineInventoryList();
        
        if (listForBatch && listForBatch.length > 0) {
          // Match by medicineId and/or name (covers duplicate catalog IDs)
          const match = resolveMedicineInventoryMatch(
            listForBatch,
            {
              medicineId: resolvedMedicineId ?? medicine.medicineId,
              medicineName: medicine.name,
            },
            { allowMissingExpiry: true }
          );
          if (match.resolvedMedicineId != null) {
            resolvedMedicineId = match.resolvedMedicineId;
          }

          const medicineInventory = match.items.filter((item) =>
            isUsableInventoryBatch(item, { allowMissingExpiry: true })
          );
          
          if (medicineInventory.length > 0) {
            // Sort by expiry date (FEFO - First Expiry First Out)
            // Items without expiry dates are treated as having infinite expiry (sorted last)
            const sortedInventory = medicineInventory.sort((a, b) => {
              const dateA = a.expiryDate || a.expiry_date || a.expirydate;
              const dateB = b.expiryDate || b.expiry_date || b.expirydate;
              
              // If both have expiry dates, sort by date
              if (dateA && dateB) {
                return new Date(dateA) - new Date(dateB);
              }
              // If only A has expiry date, A comes first
              if (dateA && !dateB) {
                return -1;
              }
              // If only B has expiry date, B comes first
              if (!dateA && dateB) {
                return 1;
              }
              // If neither has expiry date, maintain original order
              return 0;
            });
            
            // Get the batch that will expire first (among non-expired batches)
            const selectedBatch = sortedInventory[0];
            batchNumber = selectedBatch.batchNumber || selectedBatch.batch_number || "N/A";
            expiryDate = selectedBatch.expiryDate || selectedBatch.expiry_date || selectedBatch.expirydate || null;
            inventoryId = selectedBatch.id; // Capture the inventory ID
            const batchMedicineId =
              selectedBatch.medicineId ?? selectedBatch.medicine_id;
            if (batchMedicineId != null) {
              resolvedMedicineId = batchMedicineId;
            }
            supplierFields = pickInventorySupplierFields(selectedBatch);
          } else {
            // No available inventory found
            console.warn(`No available inventory found for medicine: ${medicine.name}`);
            // Set default values but don't throw error - let backend handle it
            batchNumber = "N/A";
            expiryDate = null;
            inventoryId = null;
            
            // Show warning to user about this specific medicine
            showAlert(`Warning: No available inventory found for ${medicine.name}. This medicine will be skipped.`, "warning");
          }
        }
      } catch (inventoryError) {
        console.error(`Error fetching inventory data for ${medicine.name}:`, inventoryError);
      }

      if (!resolvedMedicineId) {
        throw new Error(`Medicine ID is undefined for ${medicine.name}`);
      }

      const costPerUnit = await getCostPerUnitById(resolvedMedicineId);
      if (costPerUnit === undefined || costPerUnit === null) {
        throw new Error(`Cost per unit not found for ${medicine.name}`);
      }
      
      const totalQuantity = calculateTotalDosage(medicine.dosage) * Number(durations[medicine.name] || 0);
      const totalCost = totalQuantity * costPerUnit; // Calculate total cost for this medicine

      // Log for debugging
    //   console.log(`Medicine: ${medicine.name}, Total Quantity: ${totalQuantity}, Cost Per Unit: ${costPerUnit}, Total Cost: ${totalCost}`);

      return {
        ...medicine,
        medicineId: resolvedMedicineId,
        duration: durations[medicine.name] || "",
        totalQuantity,
        costPerUnit,
        totalCost, // Add total cost to the medicine data
        // Add additional tracking information
        batchNumber: batchNumber,
        expiryDate: expiryDate,
        manufacturer: medicine.manufacturer || "N/A",
        medicineType: medicine.medicineType || "N/A",
        medicineInventoryId: inventoryId, // Add the captured inventory ID
        supplierId: supplierFields.supplierId,
        supplierName: supplierFields.supplierName,
        stockInvoiceId: supplierFields.stockInvoiceId,
      };
    }));

    // Calculate total amount for the invoice
    const invoiceTotalAmount = medicineData.reduce((total, med) => total + med.totalCost, 0);
    if (isNaN(invoiceTotalAmount) || invoiceTotalAmount <= 0) {
      showAlert(" No stock available for this medicine, Please add stock and try again.", "error");
      return;
    }
    setTotalAmount(invoiceTotalAmount);

    const { sgst: calculatedSgst, cgst: calculatedCgst } = calcInvoiceGstBreakdown(
      invoiceTotalAmount,
      gstPercentage
    );
    setSgst(calculatedSgst);
    setCgst(calculatedCgst);

    setPaymentMode({ mode: "cash" });
    setParticulars("");
    setAttachmentFile(null);
    setAttachmentName("");
    setInvoiceDetails(medicineData); // Set invoice details for review
    setInvoiceCreated(false);
    setReviewDialogOpen(true); // Open the review dialog

  } catch (error) {
    console.error("Error processing medicines:", error);
    
    // Provide user-friendly error messages
    let errorMessage = "Failed to process medicines";
    if (error.message.includes("Medicine ID is undefined")) {
      errorMessage = "Medicine information is incomplete. Please refresh and try again.";
    } else if (error.message.includes("Cost per unit not found")) {
      errorMessage = "Medicine pricing information is missing. Please contact support.";
    } else if (error.message.includes("inventory")) {
      errorMessage = "Inventory issue detected. Please check stock levels.";
    } else {
      errorMessage = `Error: ${error.message}`;
    }
    
    showAlert(errorMessage, "error");
  } finally {
    setLoading(false);
  }
};

  const handleConfirmInvoice = async () => {
    try {
      setLoading(true);

      if (!paymentMode?.mode) {
        showAlert("Please select a payment mode.", "error");
        setLoading(false);
        return;
      }
      if (paymentMode.mode === "credit" && !paymentMode.organizationId) {
        showAlert("Please select an organization for credit payment.", "error");
        setLoading(false);
        return;
      }
      
      // Calculate final amount after discount
      const amountAfterDiscount = totalAmount - (discount || 0);
      const {
        sgst: calculatedSgst,
        cgst: calculatedCgst,
        totalAmount: totalInvoiceAmount,
      } = calcInvoiceGstBreakdown(amountAfterDiscount, gstPercentage);

      let attachmentUrl = null;
      if (attachmentFile instanceof File) {
        const uploaded = await uploadAttachment(attachmentFile);
        attachmentUrl = resolveAttachmentUrl(uploaded);
      }

      const paymentModeValue = (paymentMode?.mode || "cash").toUpperCase();
      const creditOrganizationId =
        paymentModeValue === "CREDIT"
          ? paymentMode?.organizationId || null
          : null;

      const hospitalOrgId = getOrganizationId(user);
      const hospitalFromName =
        organizationName || getOrganizationName(user) || "Aryan Hospital";

      // Copy supplier from stock purchase invoice (via inventory batch) so
      // transaction history can resolve supplier name from this issue invoice.
      const supplierInfo = await resolveSupplierForIssueInvoice(invoiceDetails);

      const resolvedDepartmentId = await resolveInvoiceDepartmentId({
        departmentId,
        doctorId,
      });

      const resolvedPrescriptionId = resolveInvoicePrescriptionId(
        prescriptionId
      );

      // Create invoice first to get invoice ID
      const invoice = await createInvoice({
        amount: totalAmount, // Base amount (before discount and tax)
        discount: discount || 0, // Discount amount
        sgst: calculatedSgst, // SGST
        cgst: calculatedCgst, // CGST
        totalAmount: totalInvoiceAmount, // Total amount including tax after discount
        date: new Date().toISOString(),
        particulars: particulars?.trim() || "MEDICINE",
        invoiceType: "MEDICINE", // Set invoice type
        patientId: patientId, // Replace with actual patient ID
        doctorId:
          doctorId != null && doctorId !== "" ? Number(doctorId) : undefined,
        appointmentId:
          appointmentId != null && appointmentId !== ""
            ? Number(appointmentId)
            : undefined,
        prescriptionId: resolvedPrescriptionId,
        departmentId: resolvedDepartmentId,
        fromName: hospitalFromName,
        toName: patientName || null,
        organizationId: hospitalOrgId || undefined,
        paymentMode: paymentModeValue,
        creditOrganizationId,
        supplierId: supplierInfo.supplierId || null,
        ...(supplierInfo.supplierName
          ? { supplierName: supplierInfo.supplierName }
          : {}),
        ...(attachmentUrl ? { attachment: attachmentUrl } : {}),
      });

      // Prepare comprehensive transaction data for each medicine
      const transactionData = {
        invoiceId: parseInt(invoice.id) || 0,
        patientId: parseInt(patientId) || 0,
        userId: parseInt(user?.id) || 0,
        prescriptionId: resolvedPrescriptionId || undefined,
        patientType: "opd", // OPD patient type
        transactionType: "OUT", // Medicine issued = OUT transaction
        transactionDate: new Date().toISOString(),
        patientName: patientName || null, // ✅ Pass patient name to backend
        doctorName: doctorName || null, // ✅ Pass doctor name to backend
        medicines: invoiceDetails.map(medicine => ({
          medicineId: parseInt(medicine.medicineId) || 0,
          medicineInventoryId: parseInt(medicine.medicineInventoryId) || 0, // Use the captured inventory ID
          medicineName: medicine.name || "",
          dosage: medicine.dosage || "",
          duration: parseInt(durations[medicine.name]) || 0,
          costPerUnit: parseFloat(medicine.costPerUnit) || 0.0,
          totalCost: parseFloat(medicine.totalCost) || 0.0,
          batchNumber: medicine.batchNumber || "N/A",
          expiryDate: medicine.expiryDate || null,
          manufacturer: medicine.manufacturer || "N/A",
          medicineType: medicine.medicineType || "N/A"
        }))
      };

      try {
        // Enhanced validation before sending request
        console.log("Sending transaction data:", transactionData);
        
        // Validate that all medicines have valid inventory IDs
        const invalidMedicines = transactionData.medicines.filter(med => !med.medicineInventoryId || med.medicineInventoryId === 0);
        if (invalidMedicines.length > 0) {
          console.warn("Some medicines don't have valid inventory IDs:", invalidMedicines);
          // Show user-friendly message about inventory issues
          showAlert(`Warning: Some medicines may not have sufficient inventory. The system will attempt to process the request, but you may need to add fresh stock for: ${invalidMedicines.map(m => m.medicineName).join(', ')}`, "warning");
        }
        
        // Validate required fields
        if (!transactionData.patientId || !transactionData.userId || !transactionData.invoiceId) {
          console.error("Missing required fields:", {
            patientId: transactionData.patientId,
            userId: transactionData.userId,
            invoiceId: transactionData.invoiceId
          });
          showAlert("Missing required information. Please refresh and try again.", "error");
          return;
        }
        
        // Validate medicines array
        if (!transactionData.medicines || transactionData.medicines.length === 0) {
          console.error("No medicines in transaction data");
          showAlert("No medicines selected. Please add medicines and try again.", "error");
          return;
        }
        
        // Filter out medicines without valid inventory IDs
        const validMedicines = transactionData.medicines.filter(med => 
          med.medicineInventoryId && med.medicineInventoryId !== 0 && med.medicineInventoryId !== null
        );
        
        const medicinesWithoutInventory = transactionData.medicines.filter(med => 
          !med.medicineInventoryId || med.medicineInventoryId === 0 || med.medicineInventoryId === null
        );
        
        if (medicinesWithoutInventory.length > 0) {
          console.warn("Medicines without valid inventory:", medicinesWithoutInventory);
          const medicineNames = medicinesWithoutInventory.map(m => m.medicineName).join(', ');
          showAlert(`Warning: The following medicines have no available inventory and will be skipped: ${medicineNames}`, "warning");
        }
        
        if (validMedicines.length === 0) {
          showAlert("No medicines with available inventory found. Please check stock levels and try again.", "error");
          return;
        }
        
        // Update transaction data to only include medicines with valid inventory
        transactionData.medicines = validMedicines;
        
        // Try to deduct inventory with comprehensive transaction tracking
        await deductMedicineInventoryWithHistory(transactionData);

        // Flag near-expiry FEFO batches for low-stock / reorder (backend when available)
        await Promise.all(
          validMedicines
            .filter((med) => med.expiryDate && isNearExpiry(med.expiryDate))
            .map((med) =>
              triggerNearExpiryLowStockAlert({
                medicineId: med.medicineId,
                medicineName: med.medicineName,
                batchNumber: med.batchNumber,
                expiryDate: med.expiryDate,
                quantity: med.totalQuantity || med.quantity,
                source: "OPD_ISSUE",
              })
            )
        );

        const nearExpiryIssued = validMedicines.filter(
          (med) => med.expiryDate && isNearExpiry(med.expiryDate)
        );
        if (nearExpiryIssued.length > 0) {
          showAlert(
            `Issued successfully. ${nearExpiryIssued.length} item(s) used near-expiry stock (within ${formatExpiryThresholdLabel(expiryThresholdMonths)}). Consider reordering.`,
            "warning"
          );
        } else {
          showAlert("Medicines issued and inventory updated successfully", "success");
        }

        setInvoiceCreated(true);
        onSubmit(invoiceDetails);
        // Keep review dialog open so user can Print Invoice after creation
        
      } catch (transactionError) {
        console.warn("Transaction tracking failed:", transactionError);
        
        // Enhanced logging for debugging while keeping user-friendly messages
        if (transactionError.response?.status === 400) {
          const errorMessage = transactionError.response?.data?.message || transactionError.message;
          const fullErrorDetails = transactionError.response?.data;
          
          // Log detailed error information for debugging
          console.error("Backend Error Details:", {
            status: transactionError.response.status,
            message: errorMessage,
            fullResponse: fullErrorDetails,
            requestPayload: transactionData
          });
          
          // Check for specific inventory-related errors
          if (errorMessage.includes("No non-expired inventory available") || 
              errorMessage.includes("No inventory found") ||
              errorMessage.includes("Insufficient") ||
              errorMessage.includes("inventory")) {
            showAlert(`Inventory Issue: ${errorMessage}. Please check stock levels and add fresh inventory if needed.`, "error");
          } else if (errorMessage.includes("Request failed with status code 400") || 
                     errorMessage.includes("status code 400") ||
                     errorMessage.includes("400 Bad Request")) {
            // Handle generic 400 errors without showing technical details
            showAlert("Unable to process medicine request. Please check inventory levels and try again.", "error");
          } else {
            // For other specific error messages, show them but clean them up
            const cleanMessage = errorMessage.replace(/Request failed with status code \d+/g, "").trim();
            if (cleanMessage && cleanMessage !== errorMessage) {
              showAlert(`Unable to process medicine request: ${cleanMessage}`, "error");
            } else {
              showAlert("Unable to process medicine request. Please check inventory levels and try again.", "error");
            }
          }
        } else {
          // Generic error handling
          console.error("Non-400 error:", transactionError);
          showAlert("Failed to process medicine request. Please try again or contact support.", "error");
        }
        
        // Don't re-throw the error to prevent the generic error handler from showing
        return;
      }
      
    } catch (error) {
      console.error("Error creating invoice:", error);
      
      // Provide more specific error messages without showing technical backend details
      let errorMessage = "Failed to process request";
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const serverMessage = error.response.data?.message || error.response.statusText;
        
        if (status === 400) {
          // Handle 400 errors with user-friendly messages
          if (serverMessage.includes("inventory") || serverMessage.includes("stock")) {
            errorMessage = `Inventory Issue: ${serverMessage}`;
          } else if (serverMessage.includes("Request failed with status code 400") || 
                     serverMessage.includes("status code 400") ||
                     serverMessage.includes("400 Bad Request")) {
            // Hide generic 400 error messages
            errorMessage = "Unable to process medicine request. Please check inventory levels and try again.";
          } else {
            // Clean up any technical error messages
            const cleanMessage = serverMessage.replace(/Request failed with status code \d+/g, "").trim();
            if (cleanMessage && cleanMessage !== serverMessage) {
              errorMessage = `Request Error: ${cleanMessage}`;
            } else {
              errorMessage = "Unable to process medicine request. Please check inventory levels and try again.";
            }
          }
        } else if (status === 500) {
          errorMessage = "Server Error: Please try again later or contact support";
        } else if (status === 404) {
          errorMessage = "Service not found: Please contact support";
        } else {
          errorMessage = `Server Error (${status}): Please try again or contact support`;
        }
        
        console.error("Server error response:", error.response.data);
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = "Network Error: Unable to connect to server. Please check your internet connection.";
        console.error("Network error:", error.request);
      } else {
        // Something else happened - clean up technical error messages
        const cleanMessage = error.message.replace(/Request failed with status code \d+/g, "").trim();
        if (cleanMessage && cleanMessage !== error.message) {
          errorMessage = `Error: ${cleanMessage}`;
        } else {
          errorMessage = "An unexpected error occurred. Please try again.";
        }
      }
      
      showAlert(errorMessage, "error");
    } finally {
      setLoading(false);
      // Keep review dialog open after successful create (for Print Invoice)
    }
  };

  return (
    <>
      <Dialog 
  open={open} 
  onClose={onClose} 
  maxWidth="md" 
  fullWidth
  sx={STANDARD_FORM_DIALOG_PAPER_SX}
>
  <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
    Issue OPD Medicines
  </DialogTitle>
  <DialogContent>
    {Array.isArray(medicines) && medicines.length > 1 && (
      <Alert severity="info" sx={{ mb: 2 }}>
        Remove medicines the patient does not want. Only medicines listed below will be
        issued and deducted from inventory.
      </Alert>
    )}
    {stockLoadError && (
      <Alert severity="error" sx={{ mb: 2 }}>
        <AlertTitle>Stock check unavailable</AlertTitle>
        {stockLoadError}
      </Alert>
    )}
    {!stockLoadError && stockChecking && (
      <Alert severity="info" sx={{ mb: 2 }}>
        Verifying medicine inventory stock…
      </Alert>
    )}
    {!stockLoadError && !stockChecking && hasBlockingStockIssues(stockChecks) && (
      <Alert severity="error" sx={{ mb: 2 }}>
        <AlertTitle>Attention — cannot confirm issue</AlertTitle>
        One or more prescribed medicines cannot be deducted from inventory. Fix stock
        (add inventory / restock) or adjust duration / remove the medicine, then try again.
        <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 1 }}>
          {stockChecks
            .filter((c) => c.blocking)
            .map((c, i) => (
              <li key={`${c.medicineId}-${i}`}>
                <Typography variant="body2">{c.message}</Typography>
              </li>
            ))}
        </Box>
      </Alert>
    )}
    {!stockLoadError &&
      !stockChecking &&
      medicinesToIssue.length > 0 &&
      !hasBlockingStockIssues(stockChecks) &&
      stockChecks.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Inventory stock is sufficient for the quantities below. You can confirm issue.
        </Alert>
      )}
    {medicinesToIssue.length > 0 ? (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Medicine</TableCell>
            <TableCell>Dosage</TableCell>
            <TableCell>Frequency</TableCell>
            <TableCell>Duration (days)</TableCell>
            <TableCell>Total Quantity</TableCell>
            <TableCell>Stock</TableCell>
            <TableCell align="center">Remove</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {medicinesToIssue.map((medicine, index) => {
            const check = stockChecks[index];
            return (
            <TableRow
              key={`${medicine.medicineId ?? medicine.name}-${index}`}
              hover
            >
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* <MedicationIcon color="primary" fontSize="small" /> */}
            {medicine.name}
          </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {medicine.medicineType && (
                      <Chip label={`Type: ${medicine.medicineType}`} color="primary" variant="outlined" size="small" />
                    )}
                    {medicine.manufacturer && (
                      <Chip label={`Manufacturer: ${medicine.manufacturer}`} color="secondary" variant="outlined" size="small" />
                    )}
                    {medicine.batchNumber && (
                      <Chip label={`Batch: ${medicine.batchNumber}`} color="default" variant="outlined" size="small" />
                    )}
                  </Box>
                  {medicine.remarks && (
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                      <strong>Remarks:</strong> {medicine.remarks}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
            <TextField
              value={medicine.dosage}
              disabled
                  size="small"
                  fullWidth
                  sx={{ maxWidth: '100px' }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  value={medicine.frequency || "As prescribed"}
                  disabled
                  size="small"
              fullWidth
                  sx={{ maxWidth: '120px' }}
            />
              </TableCell>
              <TableCell>
            <TextField
              type="number"
              value={durations[medicine.name] || ""}
              onChange={(e) => {
                      const raw = e.target.value;
                      const value = parseInt(raw);
                      if (isNaN(value)) {
                        setDurations({ ...durations, [medicine.name]: "" });
                        return;
                      }
                      setDurations({
                        ...durations,
                        [medicine.name]: value
                      });
                    }}
                  size="small"
                  inputProps={{ min: 1 }}
                  sx={{ maxWidth: '100px' }}
                  required
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {calculateTotalDosage(medicine.dosage) * (Math.max(0, Number(durations[medicine.name] || 0)))} units
          </Typography>
              </TableCell>
              <TableCell>
                {check ? (
                  <Box>
                    <Chip
                      size="small"
                      label={stockStatusLabel(check.status)}
                      color={
                        check.status === "OK"
                          ? "success"
                          : check.status === "INSUFFICIENT_STOCK"
                            ? "warning"
                            : "error"
                      }
                      variant="outlined"
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Available: {check.availableQty} / Need: {check.requiredQty}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {stockChecking ? "Checking…" : "—"}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">
                <Tooltip
                  title={
                    medicinesToIssue.length <= 1
                      ? "At least one medicine must remain to issue"
                      : "Patient will not receive this medicine"
                  }
                >
                  <span>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveMedicine(index)}
                      disabled={medicinesToIssue.length <= 1 || loading}
                      aria-label={`Remove ${medicine.name}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    ) : (
      <Typography color="text.secondary">
        No medicines selected to issue. Close and open again from the prescription, or
        restore medicines from the prescription list.
      </Typography>
    )}
  </DialogContent>
  <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
    <Button onClick={onClose} color="primary" variant="outlined" sx={FORM_CANCEL_BUTTON_SX} disabled={loading}>
      Cancel
    </Button>
    <Button 
      onClick={handleSave} 
      color="primary" 
      variant="contained"
      sx={FORM_SUBMIT_BUTTON_SX}
      disabled={
        medicinesToIssue.length === 0 ||
        loading ||
        stockChecking ||
        !!stockLoadError ||
        hasBlockingStockIssues(stockChecks) ||
        medicinesToIssue.some(
          (m) => !durations[m.name] || Number(durations[m.name]) < 1
        )
      }
    >
      {loading ? "Processing..." : "Confirm Issue"}
    </Button>
  </DialogActions>
</Dialog>

  {/* Review Invoice Dialog */}
<Dialog 
  open={reviewDialogOpen} 
  onClose={() => setReviewDialogOpen(false)} 
  maxWidth="md" 
  fullWidth
  sx={STANDARD_FORM_DIALOG_PAPER_SX}
>
  <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
    Review OPD Invoice Details
  </DialogTitle>
  <DialogContent>
    {invoiceDetails.some((m) => m.expiryDate && isNearExpiry(m.expiryDate)) && (
      <Alert severity="warning" sx={{ mb: 2 }}>
        One or more medicines use batches expiring within{" "}
        {formatExpiryThresholdLabel(expiryThresholdMonths)}. A low-stock / reorder alert
        will be raised after issue.
      </Alert>
    )}
    {invoiceDetails.length > 0 && (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Medicine Details</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 1 }}>#</TableCell>
              <TableCell sx={{ py: 1 }}>Medicine Name</TableCell>
              <TableCell sx={{ py: 1 }}>Batch Number</TableCell>
              <TableCell sx={{ py: 1 }}>Expiry Date</TableCell>
              <TableCell sx={{ py: 1 }}>Quantity</TableCell>
              <TableCell sx={{ py: 1 }}>Cost/Unit</TableCell>
              <TableCell sx={{ py: 1 }}>Total Cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoiceDetails.map((medicine, index) => (
              <TableRow key={`${medicine.name}-${index}`}>
                <TableCell sx={{ py: 1 }}>{index + 1}</TableCell>
                <TableCell sx={{ py: 1 }}>{medicine.name}</TableCell>
                <TableCell sx={{ py: 1 }}>{medicine.batchNumber || "N/A"}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  {medicine.expiryDate ? new Date(medicine.expiryDate).toLocaleDateString() : "N/A"}
                </TableCell>
                <TableCell sx={{ py: 1 }}>{medicine.totalQuantity} units</TableCell>
                <TableCell sx={{ py: 1 }}>Rs {medicine.costPerUnit?.toFixed(2)}</TableCell>
                <TableCell sx={{ py: 1 }}>Rs {medicine.totalCost?.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )}
    <Box>
      <InvoiceSummarySection
        subtotal={totalAmount - (discount || 0)}
        gstPercentage={gstPercentage}
        paymentModeValue={paymentMode}
        onPaymentModeChange={setPaymentMode}
        paymentDisabled={loading || invoiceCreated}
        attachmentField={
          <InvoiceAttachmentControl
            attachmentName={attachmentName}
            attachmentFile={attachmentFile}
            onFileChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setAttachmentFile(file);
              setAttachmentName(file.name);
            }}
            onDelete={() => {
              setAttachmentFile(null);
              setAttachmentName("");
            }}
            disabled={loading || invoiceCreated}
          />
        }
        remarksName="particulars"
        remarksValue={particulars}
        onRemarksChange={(e) => setParticulars(e.target.value)}
        remarksDisabled={loading || invoiceCreated}
        discountField={
          <>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ flexShrink: 0 }}
            >
              Total Discount
            </Typography>
            <TextField
              size="small"
              type="number"
              placeholder="0"
              value={discount || ""}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0;
                setDiscount(Math.max(0, Math.min(value, totalAmount)));
              }}
              disabled={loading || invoiceCreated}
              inputProps={{ min: 0, max: totalAmount, step: 0.01 }}
              sx={{
                width: 88,
                ml: "auto",
                "& .MuiInputBase-root": { height: 32 },
                "& .MuiInputBase-input": {
                  py: 0.5,
                  px: 1,
                  fontSize: "0.8125rem",
                },
              }}
            />
          </>
        }
      />
    </Box>
  </DialogContent>
  <DialogActions sx={{ ...STANDARD_FORM_DIALOG_ACTIONS_SX, '@media print': { display: 'none' } }}>
    {invoiceCreated && (
      <Button 
        onClick={handlePrint} 
        color="primary" 
        variant="outlined"
        sx={FORM_CANCEL_BUTTON_SX}
      >
        Print Invoice
      </Button>
    )}
    <Button
      onClick={() => {
        setReviewDialogOpen(false);
        if (invoiceCreated) {
          onClose();
        }
      }}
      color="primary"
      variant="outlined"
      sx={FORM_CANCEL_BUTTON_SX}
      disabled={loading}
    >
      {invoiceCreated ? "Close" : "Cancel"}
    </Button>
    {!invoiceCreated && (
      <Button
        onClick={handleConfirmInvoice}
        color="primary"
        variant="contained"
        sx={FORM_SUBMIT_BUTTON_SX}
        disabled={
          loading ||
          !paymentMode?.mode ||
          (paymentMode?.mode === "credit" && !paymentMode?.organizationId)
        }
      >
        {loading ? "Processing..." : "Confirm Invoice"}
      </Button>
    )}
  </DialogActions>
</Dialog>

  {/* Alert Popup */}
  <Dialog 
    open={alertOpen} 
    onClose={handleCloseAlert}
    maxWidth="xs"
    fullWidth
    PaperProps={{
      sx: {
        maxWidth: 360,
        m: 2,
      },
    }}
  >
    <Alert 
      severity={alertType} 
      onClose={handleCloseAlert}
      sx={{ 
        fontSize: '0.875rem',
        py: 1,
        alignItems: 'flex-start',
        '& .MuiAlert-message': {
          width: '100%',
          py: 0.25,
        },
        '& .MuiAlert-icon': {
          py: 0.5,
          fontSize: '1.25rem',
        },
      }}
    >
      <AlertTitle sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 0.5 }}>
        {alertType === "success" ? "Success!" : alertType === "warning" ? "Warning!" : "Error!"}
      </AlertTitle>
      {alertMessage}
    </Alert>
</Dialog>

    </>
  );
};

export default IssueMedicineModal;
